import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AppSettings } from '../../app.settings';
import { ArtworkCardSize } from '../ds-artwork-card/ds-artwork-card.component';

/**
 * Responsive artwork grid + a per-grid S/M/L size control.
 *
 * Owns three things so every artwork list behaves identically: the responsive grid
 * layout, the size (density) control, and per-context persistence (localStorage, keyed
 * by `context`). Cards are projected in; expose the chosen size to them with a template
 * ref, e.g.:
 *
 *   <ds-artwork-grid context="gallery-inventory" #g>
 *     @for (p of items; track p.Id) {
 *       <ds-artwork-card [size]="g.size" [imageUrl]="..." ...></ds-artwork-card>
 *     }
 *   </ds-artwork-grid>
 */
@Component({
    selector: 'ds-artwork-grid',
    templateUrl: './ds-artwork-grid.component.html',
    styleUrls: ['./ds-artwork-grid.component.css'],
    standalone: false
})
export class DsArtworkGridComponent implements OnInit {
    /** Distinguishes this grid's saved size from others (e.g. 'gallery-inventory'). */
    @Input() context = 'default';
    @Input() showSizeControl = true;

    /**
     * Size, when the caller owns it rather than the browser.
     *
     * Left unbound (the existing callers) the grid behaves as it always has: a viewer preference
     * remembered per browser in localStorage. Bind it and the caller becomes the source of truth —
     * used by the website builder, where card size is part of the artist's published design and
     * must not be a per-visitor setting.
     */
    @Input() set size(value: ArtworkCardSize) {
        if (!value) { return; }
        this._size = value;
        this.sizeOwnedByCaller = true;
    }
    public get size(): ArtworkCardSize { return this._size; }

    /** Emitted when the artist/user picks a size, so a caller that owns it can store it. */
    @Output() sizeChange = new EventEmitter<ArtworkCardSize>();

    private _size: ArtworkCardSize = 'md';
    private sizeOwnedByCaller = false;

    private readonly minBySize: Record<ArtworkCardSize, number> = { sm: 140, md: 200, lg: 270 };

    public ngOnInit(): void {
        // A bound size wins: reading localStorage here would overwrite it, since inputs are set
        // before ngOnInit runs.
        if (this.sizeOwnedByCaller) { return; }
        const saved = this.read();
        if (saved) { this._size = saved; }
    }

    public get minPx(): number { return this.minBySize[this._size]; }

    public setSize(s: ArtworkCardSize): void {
        this._size = s;
        this.sizeChange.emit(s);
        if (this.sizeOwnedByCaller) { return; }
        try { localStorage.setItem(this.key(), s); } catch { /* storage unavailable — ignore */ }
    }

    private read(): ArtworkCardSize | null {
        try {
            const v = localStorage.getItem(this.key());
            return (v === 'sm' || v === 'md' || v === 'lg') ? v : null;
        } catch { return null; }
    }

    private key(): string { return AppSettings.ARTWORK_CARD_SIZE_PREFIX + this.context; }
}
